import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { AcademyMyPageTabItems, AcademyMyPageLogoutButton } from "@/pages/academy/mypage/info";
import * as styles from 'components/organisms/Table/Table.css'
import Pagination from 'components/atoms/Pagination/Pagination';
import { Icon } from '@/icons';
import { Input } from 'components/atoms/Input/Input';
import { academyPaperApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { getSlugPrefix } from '@/utils/slug';

interface PurchasedPaper {
    saleId: number
    paperId: number
    name: string
    gradingType: 'AI 채점' | '일반 채점'
    aiScoring?: boolean
    purchaseDate?: string | null
    paymentDate?: string | null
    remainingTickets: number
    totalTickets: number
    availableTickets?: number
    assignedTickets?: number
    canRefund?: boolean
}

const formatPaymentDate = (value?: string | null) => {
    if (!value) return '-';
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : '-'; // [QA Fix] 결제일 미존재 시 대시 표기
};

// 컬럼 렌더링용 메타 정보
const columns = [
    { key: 'saleId' as const, render: (row: PurchasedPaper) => row.saleId },
    { key: 'name' as const, render: (row: PurchasedPaper) => row.name },
    { key: 'gradingType' as const, render: (row: PurchasedPaper) => renderGradingType(row.gradingType) },
    { key: 'purchaseDate' as const, render: (row: PurchasedPaper) => (
        <div className="flex gap-2 items-center lg:block">
            <span className="text-xs text-gray-400 lg:hidden">결제일</span>
            <span>{formatPaymentDate(row.purchaseDate || row.paymentDate)}</span>
        </div>
    ) },
    { key: 'tickets' as const, render: (row: PurchasedPaper) => (
        <div className="flex gap-2 items-center lg:block">
            <span className="text-xs text-gray-400 lg:hidden">응시권</span>
            <span>{(row.availableTickets ?? row.remainingTickets)}/{row.totalTickets}</span>
        </div>
    ) },
]

type ColumnKey = (typeof columns)[number]['key'];

const columnClassNameMap: Record<ColumnKey, string> = {
    saleId: 'max-w-[80px] !hidden lg:!flex justify-center',
    name: 'col-span-2 lg:col-span-1',
    gradingType: 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center',
    purchaseDate: 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1',
    tickets: 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1',
};

function renderGradingType(type: PurchasedPaper['gradingType']) {
    switch (type) {
        case 'AI 채점':
            return <span className="font-medium text-blue-600">AI 채점</span>
        case '일반 채점':
            return <span className="font-medium text-gray-600">일반 채점</span>
        default:
            return <span>-</span>
    }
}

export default function AcademyMypagePaper() {
    const [activeTab, setActiveTab] = useState<'info' | 'plan' | 'paper' | 'payment' | 'class'>('paper');
    const navigate = useNavigate();
    const location = useLocation();
    const { slug: sessionSlug, role: sessionRole } = useSessionSlug();
    const slugPrefix = sessionSlug ? `/${sessionSlug}` : getSlugPrefix(location.pathname);
    const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
    const isTeacher = (sessionRole || storedRole || '').toUpperCase() === 'TEACHER';
    
    // 상태 관리
    const [papers, setPapers] = useState<PurchasedPaper[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const pageSize = 10;
    
    useEffect(() => {
        if (isTeacher) {
            navigate(`${slugPrefix}/academy/mypage/info`, { replace: true });
        }
    }, [isTeacher, slugPrefix, navigate]);
    
    const loadPapers = useCallback(async () => {
        try {
            setLoading(true);
            const res: any = await academyPaperApi.getPurchased({
                q: searchQuery,
                page: currentPage - 1,
                size: pageSize
            });
            
            const data = res?.data || res;
            const content = (data?.content || []).map((item: any) => ({
                ...item,
                paperId: item.paperId ?? item.id,
                saleId: item.saleId ?? item.id,
                purchaseDate: item.purchaseDate || item.paymentDate || ''
            }));
            
            setPapers(content);
            setTotalElements(data?.totalElements || 0);
        } catch (error) {
            console.error('시험지 목록 조회 실패:', error);
            setPapers([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, currentPage, pageSize]);
    
    // 데이터 로드
    useEffect(() => {
        if (isTeacher) return;
        loadPapers();
    }, [isTeacher, loadPapers]);
    
    const handleRowClick = (paper: PurchasedPaper) => {
        const saleQuery = paper.saleId ? `?saleId=${paper.saleId}` : '';
        const paperId = paper.paperId || paper.saleId;
        navigate(`/academy/mypage/paper/detail/${paperId}${saleQuery}`);
    };
    
    const handleSearch = (value: string) => {
        setSearchQuery(value);
        setCurrentPage(1); // 검색 시 첫 페이지로
    };
    
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    
    return (
        <main className='flex flex-col flex-1'>
            <div className='flex flex-row justify-between items-center pt-8'>
                <h1 className='heading1'>마이페이지</h1>
                <AcademyMyPageLogoutButton />
            </div>
            <div className="flex flex-col flex-1 mt-5">
                <AcademyMyPageTabItems activeTab={activeTab} setActiveTab={setActiveTab} />
                <div className='flex flex-col flex-1'>
                    <section className='flex flex-col gap-2 items-start my-5 w-full'>
                    <Input
                    placeholder='시험명으로 검색해주세요.'
                    className='max-w-[500px]'
                    icon={<Icon name="search" size={20}  />}
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    />
                    </section>
                    
                    <section className='flex flex-col flex-1 justify-between w-full'>
            {loading ? (
                <div className='flex justify-center items-center py-20'>
                    <p className='text-gray-500'>로딩 중...</p>
                </div>
            ) : papers.length === 0 ? (
                <div className='flex justify-center items-center py-20'>
                    <p className='text-gray-500'>구매한 시험지가 없습니다.</p>
                </div>
            ) : (
            <div className='w-full'>
                {/* 테이블 해더 pc 용*/}
                <ul className={`${styles.academyHeaderList} w-full`}>
                    <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                    <li className={`${styles.headerItem}`}>시험명</li>
                    <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>채점유형</li>
                    <li className={`${styles.headerItem} max-w-[200px]`}>결제일</li>
                    <li className={`${styles.headerItem} max-w-[200px]`}>잔여 응시권 수/ 구매 응시권 수</li>
                </ul>
                <div className='flex flex-col gap-4 w-full lg:gap-0'>
                    {papers.map((row, index) => (
                        <ul 
                            key={row.saleId} 
                            className={`${styles.academyBodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors !grid grid-cols-2 lg:!flex`}
                            onClick={() => handleRowClick(row)}
                        >
                        {columns.map(({ key, render }) => (
                            <li
                                key={key}
                                className={`${styles.bodyItem} ${columnClassNameMap[key]}`}
                            >
                                {key === 'saleId'
                                    ? (totalElements > 0
                                        ? totalElements - ((currentPage - 1) * pageSize + index)
                                        : papers.length - index)
                                    : render(row)}
                            </li>
                        ))}
                        </ul>
                    ))}
                </div>
            </div>
            )}
                {!loading && papers.length > 0 && (
                <div className='py-5'>
            <Pagination
            total={totalElements}
            pageSize={pageSize}
            current={currentPage}
            onChange={handlePageChange}
            />
            </div>
                )}
                    </section>
                </div>
            </div>
                        
        </main>
    )
}